import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import i18n from 'i18next';
import PlayCircle from '@mui/icons-material/PlayCircle';
import OpenInNew from '@mui/icons-material/OpenInNew';
import SyncIcon from '@mui/icons-material/Sync';

import { Button } from 'uikit/buttons';
import { Tooltip } from 'uikit/tooltips';
import { Typo } from 'uikit/content';

import styles from '../styles/videoAgendaSectionItem.module.scss';
import getAgendaItemRecurringString from '../helpers/getAgendaItemRecurringString';
import getAgendaItemTimeRangeString from '../helpers/getAgendaItemTimeRangeString';
import doesAgendaItemStartWithinNextHour from '../helpers/doesAgendaItemStartWithinNextHour';
import getIsAgendaItemCurrentlyLive from '../helpers/getIsAgendaItemCurrentlyLive';
import getDoesEventStartWithinNextHourOrIsCurrentlyLive from '../helpers/getDoesEventStartWithinNextHourOrIsCurrentlyLive';

/* eslint-disable camelcase */
const VideoAgendaSectionItem = ({
  eventEndAt,
  eventStartAt,
  isEnrolled = false,
  item = {},
  selectedAgendaItem,
  viewAgendaItem,
}) => {
  const isItemSelected = selectedAgendaItem?.id && selectedAgendaItem?.id === item?.id;
  const { rrule, start_time, start_date } = item;
  const [isButtonShown, setIsButtonShown] = useState(false);
  const [isButtonEnabled, setIsButtonEnabled] = useState(false);
  const calculateIsButtonEnabled = () =>
    isEnrolled &&
    item.content &&
    (doesAgendaItemStartWithinNextHour({ agendaItem: item }) ||
      getIsAgendaItemCurrentlyLive({ agendaItem: item }));

  const calculateIsButtonShown = () =>
    isEnrolled &&
    item.content &&
    getDoesEventStartWithinNextHourOrIsCurrentlyLive({ eventStartAt, eventEndAt });

  useEffect(() => {
    setIsButtonShown(calculateIsButtonShown());
    setIsButtonEnabled(calculateIsButtonEnabled());
    const interval = setInterval(() => {
      setIsButtonShown(calculateIsButtonShown());
      setIsButtonEnabled(calculateIsButtonEnabled());
    }, 1000);
    return () => clearInterval(interval);
  }, [isEnrolled, item.id]);

  return (
    <div className={styles.container}>
      <div>
        <div className={styles.title}>
          <Typo variant="b02" weight="medium">
            {getAgendaItemTimeRangeString(item)}
          </Typo>
          {rrule.frequency && (
            <Tooltip
              className={styles.toolTip}
              content={getAgendaItemRecurringString({
                ...rrule,
                startTime: start_time,
                startDate: start_date,
              })}
              trigger="mouseenter"
            >
              <div className={styles.recurringStatus}>
                <SyncIcon sx={{ height: 18, width: 18 }} />
                <Typo variant="s01" weight="medium">
                  {i18n.t(`repeats-${rrule.frequency}`)}
                </Typo>
              </div>
            </Tooltip>
          )}
        </div>
        <div>
          <Typo variant="b01" weight="medium" className={styles.noBottomMargin}>
            {item.name}
          </Typo>
        </div>
        <div>
          <Typo variant="b01" className={styles.noBottomMargin}>
            {item.description}
          </Typo>
        </div>
        {item.speakers?.length > 0 && (
          <div>
            <Typo color="secondary" variant="s01" className={styles.speakers}>
              {i18n.t('speakers-list', {
                speakers: item.speakers?.map((speaker) => speaker.name)?.join(', '),
              })}
            </Typo>
          </div>
        )}
      </div>

      <div>
        {isButtonShown && !isItemSelected && (
          <Button
            className={styles.action_item}
            onClick={() => {
              if (item.content.is_embedded) {
                viewAgendaItem(item);
              } else {
                window.open(item.content.url, '_blank');
              }
            }}
            text={item.content.is_embedded ? i18n.t('view') : i18n.t('launch')}
            icon={item.content.is_embedded ? <PlayCircle /> : <OpenInNew />}
            iconPosition="right"
            isDisabled={!isButtonEnabled}
          />
        )}
        {isButtonShown && isItemSelected && (
          <Button
            onClick={() => {
              if (!item.content.is_embedded) {
                window.open(item.content.url, '_blank');
              }
            }}
            className={styles.action_item}
            text={item.content.is_embedded ? i18n.t('viewing') : i18n.t('launch')}
            icon={item.content.is_embedded ? <PlayCircle /> : <OpenInNew />}
            iconPosition="right"
            isDisabled={item.content.is_embedded}
          />
        )}
      </div>
    </div>
  );
};

VideoAgendaSectionItem.propTypes = {
  item: PropTypes.shape({}),
  isEnrolled: PropTypes.bool,
};

export default React.memo(VideoAgendaSectionItem);
